import React, { FC, useCallback } from 'react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
import { textFieldWMentionHelpers } from './TextFieldWMention/helpers';
import PgTypo, { PgTypoProps } from './PgTypo';
import { detectAndLinkUrls, parseTextFormatting, processEscapeSequences, restoreEscapeSequences } from './TypographyWMention/textFormatters';
import { FONT_WEIGHTS } from 'Theme/themeConstants';

export interface PgTypoWMentionProps extends PgTypoProps {
    renderMentionHTMLStr?: (name: string, meta: string) => string;
}

const PgTypoWMention: FC<PgTypoWMentionProps> = ({ children, renderMentionHTMLStr, ...props }) => {
    const classes = useStyles({});

    const renderMention = useCallback((name: string, meta: string) => {
        return `<a href="#">${name}</a>`;
    }, []);

    const processText = useCallback(
        (text: string): string => {
            const { text: escapedText, placeholders } = processEscapeSequences(text);
            const mentionedText = textFieldWMentionHelpers.getTextNode(escapedText, renderMentionHTMLStr ?? renderMention);
            const linkedText = detectAndLinkUrls(mentionedText);
            const formattedText = parseTextFormatting(linkedText);
            return restoreEscapeSequences(formattedText, placeholders);
        },
        [renderMention, renderMentionHTMLStr],
    );

    const __html = typeof children === 'string' ? processText(children) : children;

    if (typeof children === 'string') {
        return <PgTypo {...props} className={clsx(classes.root, props.className)} dangerouslySetInnerHTML={{ __html: __html as string }} />;
    }

    return <PgTypo {...props}>{__html}</PgTypo>;
};

const useStyles = makeStyles<Theme, any>((theme) => ({
    root: {
        '& a': {
            color: theme.palette.primary.main,
            textDecoration: 'underline',
            '&:hover': {
                opacity: 0.8,
            },
        },
        // Mention links (no target="_blank") keep their own style and do not inherit bold/italic from formatting
        '& strong a:not([target="_blank"]), & em a:not([target="_blank"])': {
            fontWeight: FONT_WEIGHTS.fontWeightRegular,
            fontStyle: 'normal',
        },
        '& strong': {
            fontWeight: FONT_WEIGHTS.fontWeightBold,
        },
        '& em': {
            fontStyle: 'italic',
        },
    },
}));

export default PgTypoWMention;
